import React, {useState} from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import CopyIcon from "../../../../../../../../assets/icons/CopyIcon";
import CheckIcon from "../../../../../../../../assets/icons/CheckIcon";
import colors from "../../../../../../../../utils/Colors";

/*
 * The ids an integration has to match against, on the page where it is set up.
 *
 * The company, the bot and the operator are known to us by ids nobody can
 * guess, and whoever receives the webhooks needs them to tell one from another.
 * Looking one up meant a query against the database every time.
 *
 * They are labelled with the field names rather than with a translated caption:
 * those names are what the other side writes into its own configuration, and a
 * caption would only have to be mapped back to them. Nothing is fetched - the
 * company and the signed in user are already in the store.
 */

const styles = {
    strip: {
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: '8px',
        margin: '4px 0 16px',
    },
    chip: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        maxWidth: '100%',
        background: 'var(--ds-bg-secondary)',
        border: '1px solid var(--ds-neutral-300)',
        borderRadius: '14px',
        padding: '3px 10px',
        font: 'inherit',
        fontSize: '12px',
        lineHeight: '18px',
        cursor: 'pointer',
    },
    label: {
        color: 'var(--ds-neutral-700)',
        whiteSpace: 'nowrap',
    },
    value: {
        fontFamily: 'monospace',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
};

const IntegrationIds = ({client, company, user}) => {
    const [copiedValue, setCopiedValue] = useState(null);

    const copy = (value) => () => {
        if (!value || !navigator.clipboard || !navigator.clipboard.writeText) return;
        navigator.clipboard.writeText(value).then(() => {
            setCopiedValue(value);
            setTimeout(() => setCopiedValue(null), 1000);
        }, () => setCopiedValue(null));
    };

    const chip = (label, value) => (
        <button type="button" style={styles.chip} onClick={copy(value)} title={value || ''}>
            <span style={styles.label}>{label}</span>
            <span style={styles.value}>{value || '—'}</span>
            {copiedValue === value
                ? <CheckIcon width={13} height={13} style={{color: colors.success, flexShrink: 0}}/>
                : <CopyIcon width={13} height={13} style={{flexShrink: 0}}/>}
        </button>
    );

    /*
     * The operator's own id on the chat side. It is the auth id the token
     * already carries - Message.isUserReceiver matches an incoming platformId
     * against exactly that - and the member record is preferred only because it
     * is the same value read from where the chat service itself stores it.
     */
    const me = (company.members || []).find(member => member.id === user.id);
    const platformId = (me && me.platformId) || user.id;

    return (
        <div style={styles.strip}>
            {chip('parentCompanyId', company.id)}
            {chip('companyId', client && client.id)}
            {chip('operatorPlatformId', platformId)}
        </div>
    );
};

IntegrationIds.propTypes = {
    client: PropTypes.object.isRequired,
    company: PropTypes.object.isRequired,
    user: PropTypes.object.isRequired,
};

const mapStateToProps = state => ({
    company: state.company,
    user: state.user,
});

export default connect(mapStateToProps)(IntegrationIds);



// WEBPACK FOOTER //
// ./src/scenes/dashboard/components/management/clients/new/resources/webhooks/IntegrationIds.jsx